import { CustomColumnType } from "../grid-column-types";
import { benchmarkQuartileRankingValueFormatter } from "../renderers/benchmarkQuartileRenderer";
import { dateBasedYearHelper } from "../value-helpers";
import { quartileColors, repRiskColors, rowColors, sparklineColumnSize, variationColors } from "./constants";
import { numberFormatters } from "common";
import { getDateString } from "common/date-utils";
import { NumberFormatKind } from "common/types";
const processMoneyCell = (cellValue, dataScale) => ({
    alignment: "right",
    text: [
        { text: `${numberFormatters(cellValue.amount, NumberFormatKind.compact, { maximumFractionDigits: 2, minimumFractionDigits: 2 }, dataScale)} ` },
        { style: "currency", text: cellValue.currency }
    ]
});
const processTagsCell = (cellValue) => {
    const tags = cellValue.map((tag) => ({ background: tag.color, style: "tags", text: tag.label }));
    return {
        text: tags
    };
};
const processUnitCell = (cellValue, dataScale) => ({
    alignment: "right",
    text: [
        { text: `${numberFormatters(cellValue.value, NumberFormatKind.compact, { maximumFractionDigits: 2, minimumFractionDigits: 2 }, dataScale)} ` },
        { style: "currency", text: cellValue.unit }
    ]
});
const processAreaCell = (cellValue, dataScale) => ({
    alignment: "right",
    text: [
        { text: `${numberFormatters(cellValue.area, NumberFormatKind.compact, { maximumFractionDigits: 1, minimumFractionDigits: 1 }, dataScale)} ` },
        { style: "currency", text: cellValue.unit }
    ]
});
const processDateCell = (cellValue) => ({
    alignment: "right",
    text: getDateString(new Date(cellValue))
});
const processPercentageCell = (cellValue) => ({
    alignment: "right",
    text: cellValue === null || cellValue === undefined ? null : `${(cellValue * 100).toFixed(2)}%`
});
const getPointsFunctions = (ctx, cellValue) => {
    const canvasHeight = ctx.canvas.height, canvasWidth = ctx.canvas.width;
    const padding = 3;
    const chartWidth = canvasWidth - (4 * padding);
    const chartHeight = canvasHeight - (2 * padding);
    const xValues = cellValue.map((item) => new Date(item.x.date).getTime());
    const yValues = cellValue.map((item) => item.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const fnX = (value) => (((value - xMin) / (xMax - xMin)) * chartWidth) + padding;
    const fnY = (value) => canvasHeight - padding - (((value - yMin) / (yMax - yMin)) * chartHeight);
    return { fnX, fnY };
};
const processSparklineColumnCell = (cellValue) => {
    const canvas = document.createElement("canvas");
    canvas.width = sparklineColumnSize.WIDTH;
    canvas.height = sparklineColumnSize.HEIGHT;
    const ctx = canvas.getContext("2d");
    const { fnX, fnY } = getPointsFunctions(ctx, cellValue);
    ctx.beginPath();
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1;
    cellValue.forEach((point, index) => {
        const x = fnX(new Date(point.x.date).getTime());
        const y = fnY(point.y);
        if (index === 0) {
            ctx.moveTo(x, y);
        }
        else {
            ctx.lineTo(x, y);
        }
    });
    ctx.stroke();
    return {
        image: canvas.toDataURL("image/png")
    };
};
const processPercentageVariationCell = (cellValue) => {
    const percentage = `${(cellValue * 100).toFixed(2)}%`;
    let color = variationColors.ZERO_VALUE;
    if (cellValue < 0) {
        color = variationColors.NEGATIVE_VALUE;
    }
    else if (cellValue > 0) {
        color = variationColors.POSITIVE_VALUE;
    }
    return {
        alignment: "right",
        text: [
            {
                color,
                text: percentage
            }
        ]
    };
};
const processMultiplyCell = (cellValue) => ({
    alignment: "right",
    text: `${cellValue.toFixed(2)}x`
});
const processVariationCell = (cellValue) => {
    let color = variationColors.ZERO_VALUE;
    if (cellValue < 0) {
        color = variationColors.NEGATIVE_VALUE;
    }
    else if (cellValue > 0) {
        color = variationColors.POSITIVE_VALUE;
    }
    return {
        alignment: "right",
        text: [
            {
                color,
                text: cellValue
            }
        ]
    };
};
const processQuartileRankingCell = (cellValue) => {
    if (cellValue === 0) {
        return { text: "" };
    }
    const fillColor = quartileColors[cellValue];
    return {
        alignment: "center",
        fillColor,
        text: benchmarkQuartileRankingValueFormatter({ value: cellValue })
    };
};
const processRepRiskCell = (cellValue) => {
    const repRiskValue = cellValue === "N/A" ? "NA" : cellValue;
    return {
        alignment: "center",
        fillColor: repRiskColors[repRiskValue],
        text: cellValue
    };
};
const processYearCell = (cellValue) => ({
    text: `${numberFormatters(cellValue, NumberFormatKind.compact)} year${cellValue > 1 ? "s" : ""}`
});
const processDateBasedYearCell = (cellValue, granularity) => {
    const value = dateBasedYearHelper(cellValue, { granularity });
    return {
        alignment: "left",
        text: value
    };
};
const processDefaultCell = (cellValue) => ({
    alignment: "left",
    text: cellValue
});
const getArrayTypes = (column, cellValue) => {
    switch (true) {
        case column.type.includes(CustomColumnType.InsTagsColumn):
            return processTagsCell(cellValue);
        case column.type.includes(CustomColumnType.InsPercentageVariationColumn):
            return processPercentageVariationCell(cellValue);
        case column.type.includes(CustomColumnType.InsVariationColumn) || column.type.includes(CustomColumnType.InsRightAlignedVariationColumn):
            return processVariationCell(cellValue);
        case column.type.includes(CustomColumnType.InsQuartileRanking):
            return processQuartileRankingCell(cellValue);
        case column.type.includes(CustomColumnType.InsYearsColumn):
            return processYearCell(cellValue);
        case column.type.includes(CustomColumnType.InsDateColumn):
            return processDateCell(cellValue);
        default:
            return processDefaultCell(cellValue);
    }
};
const processCell = (column, cellValue, widgetSettings) => {
    if (Array.isArray(column.type)) {
        return getArrayTypes(column, cellValue);
    }
    switch (column.type) {
        case CustomColumnType.InsMoneyColumn:
            return processMoneyCell(cellValue, widgetSettings?.dataScale || "");
        case CustomColumnType.InsUnitColumn:
            return processUnitCell(cellValue, widgetSettings?.dataScale || "");
        case CustomColumnType.InsAreaColumn:
            return processAreaCell(cellValue, widgetSettings?.dataScale || "");
        case CustomColumnType.InsDateColumn:
            return processDateCell(cellValue);
        case CustomColumnType.InsPercentageColumn:
            return processPercentageCell(cellValue);
        case CustomColumnType.InsPercentageVariationColumn:
            return processPercentageVariationCell(cellValue);
        case CustomColumnType.InsMultiplyColumn:
            return processMultiplyCell(cellValue);
        case CustomColumnType.InsRepRiskColumn:
            return processRepRiskCell(cellValue);
        case CustomColumnType.InsQuartileRanking:
            return processQuartileRankingCell(cellValue);
        case CustomColumnType.InsYearsColumn:
            return processYearCell(cellValue);
        case CustomColumnType.InsDateBasedYearColumn:
            return processDateBasedYearCell(cellValue, widgetSettings?.dataGranularity || "");
        case CustomColumnType.InsSparklineColumn:
            return processSparklineColumnCell(cellValue);
        default:
            return processDefaultCell(cellValue);
    }
};
const shouldIncludeColumnInExport = (column) => column.userProvidedColDef?.type !== CustomColumnType.AUX_CHECKBOX_COLUMN && !column.userProvidedColDef?.skipExcelExport;
export const getRowsToExport = (gridApi, widgetSettings) => {
    const visibleColumns = gridApi?.getAllDisplayedColumns()
        .filter(shouldIncludeColumnInExport)
        .map((x) => x.colDef) ?? [];
    const isPivot = gridApi.isPivotMode();
    const getCellToExport = (column, node) => {
        if (column.showRowGroup) {
            const data = node?.aggData?.[node.field] ?? node?.groupData?.["ag-Grid-AutoColumn"];
            if (isPivot && data) {
                const cell = node.rowGroupColumn.userProvidedColDef;
                return processCell(cell, data, widgetSettings);
            }
            if (column.footer) {
                return {
                    text: "Total"
                };
            }
            if (data || node.allChildrenCount) {
                return {
                    text: `${data ?? ""} (${node.allChildrenCount})`
                };
            }
            return {};
        }
        const cellValue = gridApi.getValue(column.field, node);
        if (cellValue === null || cellValue === undefined || cellValue === "") {
            return { text: "" };
        }
        return processCell(column, cellValue, widgetSettings);
    };
    const rowsToExport = [];
    gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.parent?.expanded === false || (node.parent?.expanded === true && isPivot)) {
            return;
        }
        const rowToExport = visibleColumns.map((column) => getCellToExport(column, node));
        if (node.group && !isPivot) {
            rowsToExport.push(rowToExport.map((x) => ({ ...x, bold: true, fillColor: rowColors.GROUP_ROW_COLOR, margin: [node.level * 5, 0, 0, 0] })));
        }
        else {
            rowsToExport.push(rowToExport);
        }
    });
    gridApi.getRenderedNodes().filter((x) => x.footer)
        .forEach((node) => {
        const rowToExport = visibleColumns.map((column) => getCellToExport({ ...column, footer: true }, node));
        rowsToExport.push(rowToExport.map((x) => ({ ...x, bold: true, fillColor: rowColors.HEADER_ROW_COLOR })));
    });
    return rowsToExport;
};
